import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import type { Visibility } from "../api/types";

export function CreateMeet() {
  const navigate = useNavigate();
  const teams = useAsync(() => api.listTeams(), []);
  const adminTeams = teams.data?.items.filter((t) => t.role === "admin") ?? [];

  const [form, setForm] = useState({
    title: "",
    hostTeamId: "",
    startAt: "",
    endAt: "",
    venueName: "",
    line1: "",
    city: "",
    state: "",
    zip: "",
    description: "",
    sessionDetails: "",
    format: "",
    meetType: "dual" as "individual" | "dual",
    registrationInfo: "",
    registrationUrl: "",
    visibility: "team" as Visibility,
  });
  const [err, setErr] = useState<string>();
  const [saving, setSaving] = useState(false);

  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(undefined);
    setSaving(true);
    try {
      const meet = await api.createMeet({
        title: form.title,
        description: form.description || undefined,
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
        venueName: form.venueName,
        address: {
          line1: form.line1,
          city: form.city,
          state: form.state,
          zip: form.zip,
        },
        hostTeamId: form.hostTeamId || adminTeams[0]?.teamId,
        sessionDetails: form.sessionDetails || undefined,
        format: form.format || undefined,
        meetType: form.meetType,
        registrationInfo: form.registrationInfo || undefined,
        registrationUrl: form.registrationUrl || undefined,
        visibility: form.visibility,
      });
      navigate(`/meets/${meet.meetId}`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (adminTeams.length === 0 && !teams.loading) {
    return (
      <div className="card">
        <h1>New meet</h1>
        <p className="muted">
          You need to be an admin of a team to create meets.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1>New meet</h1>
      <div className="card grid">
        <div>
          <label>Host team</label>
          <select
            value={form.hostTeamId}
            onChange={(e) => set("hostTeamId", e.target.value)}
          >
            <option value="">Select team…</option>
            {adminTeams.map((t) => (
              <option key={t.teamId} value={t.teamId}>
                {t.name ?? t.teamId}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Title</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} />
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label>Start</label>
            <input
              type="datetime-local"
              value={form.startAt}
              onChange={(e) => set("startAt", e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label>End</label>
            <input
              type="datetime-local"
              value={form.endAt}
              onChange={(e) => set("endAt", e.target.value)}
            />
          </div>
        </div>
        <div>
          <label>Venue name</label>
          <input
            value={form.venueName}
            onChange={(e) => set("venueName", e.target.value)}
          />
        </div>
        <div>
          <label>Address</label>
          <input
            placeholder="Street"
            value={form.line1}
            onChange={(e) => set("line1", e.target.value)}
          />
          <div className="row" style={{ marginTop: "0.5rem" }}>
            <input
              placeholder="City"
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
            />
            <input
              placeholder="State"
              value={form.state}
              onChange={(e) => set("state", e.target.value)}
            />
            <input
              placeholder="Zip"
              value={form.zip}
              onChange={(e) => set("zip", e.target.value)}
            />
          </div>
        </div>
        <div>
          <label>Description</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <div>
          <label>Meet type</label>
          <div className="seg" role="radiogroup" aria-label="Meet type">
            <button
              type="button"
              role="radio"
              aria-checked={form.meetType === "dual"}
              className={form.meetType === "dual" ? "seg-on" : ""}
              onClick={() => setForm((f) => ({ ...f, meetType: "dual" }))}
            >
              Dual (team vs team)
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={form.meetType === "individual"}
              className={form.meetType === "individual" ? "seg-on" : ""}
              onClick={() =>
                setForm((f) => ({ ...f, meetType: "individual" }))
              }
            >
              Individual / tournament
            </button>
          </div>
          <p className="muted" style={{ fontSize: "0.82rem", marginTop: "0.3rem" }}>
            {form.meetType === "dual"
              ? "Duals get a weight-class lineup board."
              : "Individual meets focus on RSVP + registration."}
          </p>
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label>Format</label>
            <input
              placeholder="Folkstyle / Duals / Open / WAR Zone"
              value={form.format}
              onChange={(e) => set("format", e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label>Session details</label>
            <input
              placeholder="e.g. Duals 8 AM / Open Sat 3 PM"
              value={form.sessionDetails}
              onChange={(e) => set("sessionDetails", e.target.value)}
            />
          </div>
        </div>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label>Registration info</label>
            <input
              placeholder="e.g. Online Registration Required"
              value={form.registrationInfo}
              onChange={(e) => set("registrationInfo", e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label>Registration link</label>
            <input
              placeholder="https://…"
              value={form.registrationUrl}
              onChange={(e) => set("registrationUrl", e.target.value)}
            />
          </div>
        </div>
        <div>
          <label>Visibility</label>
          <select
            value={form.visibility}
            onChange={(e) => set("visibility", e.target.value)}
          >
            <option value="team">Team only</option>
            <option value="public">Public (discoverable)</option>
          </select>
        </div>
        {err && <p className="error">{err}</p>}
        <div>
          <button onClick={submit} disabled={saving}>
            {saving ? "Creating…" : "Create meet"}
          </button>
        </div>
      </div>
    </div>
  );
}
